import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '@/utils/axios';
import { toast } from 'sonner';
import {
  normalizeBlogForList,
  normalizeBlogForDetail,
  normalizeComment,
} from '@/utils/helper/blog';

// Initial state
const initialState = {
  // Public blogs
  publishedBlogs: [],
  publishedBlogsLoading: false,
  publishedBlogsError: null,

  // Current blog (for blog details)
  currentBlog: null,
  currentBlogLoading: false,
  currentBlogError: null,

  // Admin blogs
  adminBlogs: [],
  adminBlogsLoading: false,
  adminBlogsError: null,

  // Blog creation/update
  creatingBlog: false,
  createBlogError: null,
  updatingBlog: false,
  updateBlogError: null,
  deletingBlog: false,
  deleteBlogError: null,

  // Comments
  comments: [],
  commentsLoading: false,
  commentsError: null,
  creatingComment: false,
  createCommentError: null,
  moderatingComments: false,

  // Filters (for admin)
  filters: {
    status: '',
    category: '',
    searchTerm: '',
  },

  // Pagination (for admin)
  pagination: {
    currentPage: 1,
    itemsPerPage: 10,
    total: 0,
    totalPages: 0,
  },
};

// ==================== PUBLIC BLOG THUNKS ====================

export const getPublishedBlogs = createAsyncThunk(
  'blogs/getPublishedBlogs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/blogs/published');
      // Backend returns { message: 'Published blogs fetched successfully', blogs: [...] }
      const blogs = response.data?.blogs || response.data || [];
      // Normalize blogs for display
      return blogs.map(normalizeBlogForList);
    } catch (error) {
      console.error('Failed to fetch published blogs:', error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        'Failed to fetch blogs. Please try again.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const getBlogById = createAsyncThunk(
  'blogs/getBlogById',
  async (idOrSlug, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/blogs/${idOrSlug}`);
      // Backend returns { message: 'Blog fetched successfully', blog: {...} }
      const blog = response.data?.blog || response.data;
      if (!blog) {
        throw new Error('Blog data not found in response');
      }
      // Normalize blog for detail display
      return normalizeBlogForDetail(blog);
    } catch (error) {
      console.error('Error fetching blog:', error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        'Failed to fetch blog. Please try again.';
      return rejectWithValue(message);
    }
  }
);

export const createComment = createAsyncThunk(
  'blogs/createComment',
  async ({ blogId, commentData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`/blogs/comments/${blogId}`, commentData);
      toast.success(
        response.data.approved
          ? response.data.message || 'Your comment has been posted successfully!'
          : response.data.message || 'Your comment has been submitted for review. Thank you!'
      );
      // Refresh blog to get updated comments
      // We need to get the slug from the current blog state or pass it
      return response.data;
    } catch (error) {
      console.error('Error creating comment:', error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        'Failed to add comment. Please try again.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// ==================== ADMIN BLOG THUNKS ====================

export const getAllBlogs = createAsyncThunk(
  'blogs/getAllBlogs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/blogs/all');
      // Backend returns { message: 'All blogs fetched successfully', blogs: [...] }
      const blogs = response.data?.blogs || response.data || [];
      // Normalize blogs for display
      return blogs.map((blog) => {
        const normalized = normalizeBlogForList(blog);
        // Merge with raw blog data to include admin-specific fields
        return {
          ...normalized,
          status: blog.status || 'draft',
          author: blog.author || 'Admin',
          views: blog.views || 0,
          comments: blog.commentsCount || blog.comments || 0,
          publishDate: blog.createdAt || blog.publishDate || new Date().toISOString(),
          scheduleDate: blog.scheduleDate || null,
          featuredImage: normalized.image,
        };
      });
    } catch (error) {
      console.error('Failed to fetch all blogs:', error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        'Failed to fetch blogs. Please try again.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const createBlog = createAsyncThunk(
  'blogs/createBlog',
  async (blogData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post('/blogs', blogData);
      toast.success(
        response.data.message ||
          `Blog post ${blogData.status === 'published' ? 'published' : 'saved as draft'} successfully!`
      );
      // Refresh admin blogs after creation
      dispatch(getAllBlogs());
      return response.data;
    } catch (error) {
      console.error('Error creating blog:', error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        'Failed to create blog. Please try again.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateBlog = createAsyncThunk(
  'blogs/updateBlog',
  async ({ blogId, blogData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.put(`/blogs/${blogId}`, blogData);
      toast.success(
        response.data.message ||
          `Blog post ${blogData.status === 'published' ? 'published' : 'updated'} successfully!`
      );
      // Refresh admin blogs after update
      dispatch(getAllBlogs());
      return { blogId, blog: response.data };
    } catch (error) {
      console.error('Error updating blog:', error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        'Failed to update blog. Please try again.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteBlog = createAsyncThunk(
  'blogs/deleteBlog',
  async (blogId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.delete(`/blogs/${blogId}`);
      toast.success(response.data.message || 'Blog deleted successfully!');
      // Refresh admin blogs after deletion
      dispatch(getAllBlogs());
      return { blogId, data: response.data };
    } catch (error) {
      console.error('Error deleting blog:', error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        'Failed to delete blog. Please try again.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// ==================== ADMIN COMMENT THUNKS ====================

export const getAllComments = createAsyncThunk(
  'blogs/getAllComments',
  async ({ approved, deleted, limit = 20, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (approved !== undefined) {
        queryParams.append('approved', approved);
      }
      if (deleted !== undefined) {
        queryParams.append('deleted', deleted);
      }
      queryParams.append('limit', limit);
      queryParams.append('offset', offset);

      const response = await axios.get(`/blogs/comments?${queryParams.toString()}`);
      // Backend returns { comments: [...] }
      const comments = response.data.comments || [];
      // Normalize comments for admin display
      return comments.map((comment) => {
        const normalized = normalizeComment(comment);
        // Add admin-specific fields
        return {
          ...normalized,
          email: comment.email || '',
          blogPost: comment.blogTitle || 'Unknown Blog',
          blogId: comment.blogId,
          approved: comment.approved || 0,
          deleted: comment.deleted || 0,
          moderatedBy: comment.moderatedBy || null,
          moderatedAt: comment.moderatedAt || null,
        };
      });
    } catch (error) {
      console.error('Error fetching comments:', error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        'Failed to fetch comments. Please try again.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const bulkModerateComments = createAsyncThunk(
  'blogs/bulkModerateComments',
  async ({ commentIds, action }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await axios.put('/blogs/comments/bulk-moderate', {
        commentIds,
        action,
      });
      toast.success(response.data.message || 'Action completed successfully');
      // Refresh comments after moderation
      const state = getState();
      const filters = state.blogs.filters;
      // Re-fetch comments with current filters
      const params = {};
      if (filters.status === 'approved') {
        params.approved = 1;
        params.deleted = 0;
      } else if (filters.status === 'pending') {
        params.approved = 0;
        params.deleted = 0;
      } else if (filters.status === 'spam') {
        params.deleted = 1;
      }
      dispatch(getAllComments(params));
      return response.data;
    } catch (error) {
      console.error('Error bulk moderating comments:', error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        'Failed to moderate comments. Please try again.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Blog slice
const blogSlice = createSlice({
  name: 'blogs',
  initialState,
  reducers: {
    // Clear current blog
    clearCurrentBlog: (state) => {
      state.currentBlog = null;
      state.currentBlogError = null;
    },

    // Filters (for admin)
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
      state.pagination.currentPage = 1;
    },
    setCategoryFilter: (state, action) => {
      state.filters.category = action.payload;
      state.pagination.currentPage = 1;
    },
    setSearchTerm: (state, action) => {
      state.filters.searchTerm = action.payload;
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        status: '',
        category: '',
        searchTerm: '',
      };
      state.pagination.currentPage = 1;
    },

    // Pagination
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.pagination.itemsPerPage = action.payload;
      state.pagination.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    // Get published blogs
    builder
      .addCase(getPublishedBlogs.pending, (state) => {
        state.publishedBlogsLoading = true;
        state.publishedBlogsError = null;
      })
      .addCase(getPublishedBlogs.fulfilled, (state, action) => {
        state.publishedBlogsLoading = false;
        state.publishedBlogs = action.payload;
        state.publishedBlogsError = null;
      })
      .addCase(getPublishedBlogs.rejected, (state, action) => {
        state.publishedBlogsLoading = false;
        state.publishedBlogsError = action.payload;
        state.publishedBlogs = [];
      });

    // Get blog by ID
    builder
      .addCase(getBlogById.pending, (state) => {
        state.currentBlogLoading = true;
        state.currentBlogError = null;
      })
      .addCase(getBlogById.fulfilled, (state, action) => {
        state.currentBlogLoading = false;
        state.currentBlog = action.payload;
        state.currentBlogError = null;
      })
      .addCase(getBlogById.rejected, (state, action) => {
        state.currentBlogLoading = false;
        state.currentBlogError = action.payload;
        state.currentBlog = null;
      });

    // Create comment
    builder
      .addCase(createComment.pending, (state) => {
        state.creatingComment = true;
        state.createCommentError = null;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        state.creatingComment = false;
        state.createCommentError = null;
        // Refresh current blog to get updated comments
        // This will be handled by refetching in the component
      })
      .addCase(createComment.rejected, (state, action) => {
        state.creatingComment = false;
        state.createCommentError = action.payload;
      });

    // Get all blogs (admin)
    builder
      .addCase(getAllBlogs.pending, (state) => {
        state.adminBlogsLoading = true;
        state.adminBlogsError = null;
      })
      .addCase(getAllBlogs.fulfilled, (state, action) => {
        state.adminBlogsLoading = false;
        state.adminBlogs = action.payload;
        state.pagination.total = action.payload.length;
        state.pagination.totalPages = Math.ceil(action.payload.length / state.pagination.itemsPerPage);
        state.adminBlogsError = null;
      })
      .addCase(getAllBlogs.rejected, (state, action) => {
        state.adminBlogsLoading = false;
        state.adminBlogsError = action.payload;
        state.adminBlogs = [];
      });

    // Create blog
    builder
      .addCase(createBlog.pending, (state) => {
        state.creatingBlog = true;
        state.createBlogError = null;
      })
      .addCase(createBlog.fulfilled, (state) => {
        state.creatingBlog = false;
        state.createBlogError = null;
      })
      .addCase(createBlog.rejected, (state, action) => {
        state.creatingBlog = false;
        state.createBlogError = action.payload;
      });

    // Update blog
    builder
      .addCase(updateBlog.pending, (state) => {
        state.updatingBlog = true;
        state.updateBlogError = null;
      })
      .addCase(updateBlog.fulfilled, (state, action) => {
        state.updatingBlog = false;
        state.updateBlogError = null;
        // Update in admin blogs
        const { blogId } = action.payload;
        const index = state.adminBlogs.findIndex((b) => b.id === blogId);
        if (index !== -1) {
          state.adminBlogs[index] = { ...state.adminBlogs[index], ...action.payload.blog };
        }
        // Update current blog if it's the same
        if (state.currentBlog && state.currentBlog.id === blogId) {
          state.currentBlog = { ...state.currentBlog, ...action.payload.blog };
        }
      })
      .addCase(updateBlog.rejected, (state, action) => {
        state.updatingBlog = false;
        state.updateBlogError = action.payload;
      });

    // Delete blog
    builder
      .addCase(deleteBlog.pending, (state) => {
        state.deletingBlog = true;
        state.deleteBlogError = null;
      })
      .addCase(deleteBlog.fulfilled, (state, action) => {
        state.deletingBlog = false;
        state.deleteBlogError = null;
        // Remove from admin blogs
        state.adminBlogs = state.adminBlogs.filter((b) => b.id !== action.payload.blogId);
        // Clear current blog if it's the deleted one
        if (state.currentBlog && state.currentBlog.id === action.payload.blogId) {
          state.currentBlog = null;
        }
      })
      .addCase(deleteBlog.rejected, (state, action) => {
        state.deletingBlog = false;
        state.deleteBlogError = action.payload;
      });

    // Get all comments (admin)
    builder
      .addCase(getAllComments.pending, (state) => {
        state.commentsLoading = true;
        state.commentsError = null;
      })
      .addCase(getAllComments.fulfilled, (state, action) => {
        state.commentsLoading = false;
        state.comments = action.payload;
        state.commentsError = null;
      })
      .addCase(getAllComments.rejected, (state, action) => {
        state.commentsLoading = false;
        state.commentsError = action.payload;
        state.comments = [];
      });

    // Bulk moderate comments
    builder
      .addCase(bulkModerateComments.pending, (state) => {
        state.moderatingComments = true;
      })
      .addCase(bulkModerateComments.fulfilled, (state) => {
        state.moderatingComments = false;
      })
      .addCase(bulkModerateComments.rejected, (state) => {
        state.moderatingComments = false;
      });
  },
});

export const {
  clearCurrentBlog,
  setStatusFilter,
  setCategoryFilter,
  setSearchTerm,
  clearFilters,
  setCurrentPage,
  setItemsPerPage,
} = blogSlice.actions;

// Selectors
export const selectPublishedBlogs = (state) => state.blogs.publishedBlogs;
export const selectPublishedBlogsLoading = (state) => state.blogs.publishedBlogsLoading;
export const selectPublishedBlogsError = (state) => state.blogs.publishedBlogsError;

export const selectCurrentBlog = (state) => state.blogs.currentBlog;
export const selectCurrentBlogLoading = (state) => state.blogs.currentBlogLoading;
export const selectCurrentBlogError = (state) => state.blogs.currentBlogError;

export const selectAdminBlogs = (state) => state.blogs.adminBlogs;
export const selectAdminBlogsLoading = (state) => state.blogs.adminBlogsLoading;
export const selectAdminBlogsError = (state) => state.blogs.adminBlogsError;

export const selectCreatingBlog = (state) => state.blogs.creatingBlog;
export const selectCreateBlogError = (state) => state.blogs.createBlogError;
export const selectUpdatingBlog = (state) => state.blogs.updatingBlog;
export const selectUpdateBlogError = (state) => state.blogs.updateBlogError;
export const selectDeletingBlog = (state) => state.blogs.deletingBlog;
export const selectDeleteBlogError = (state) => state.blogs.deleteBlogError;

export const selectComments = (state) => state.blogs.comments;
export const selectCommentsLoading = (state) => state.blogs.commentsLoading;
export const selectCommentsError = (state) => state.blogs.commentsError;
export const selectCreatingComment = (state) => state.blogs.creatingComment;
export const selectCreateCommentError = (state) => state.blogs.createCommentError;
export const selectModeratingComments = (state) => state.blogs.moderatingComments;

export const selectBlogFilters = (state) => state.blogs.filters;
export const selectBlogPagination = (state) => state.blogs.pagination;

// Computed selectors
export const selectFilteredAdminBlogs = (state) => {
  const blogs = state.blogs.adminBlogs;
  const { status, category, searchTerm } = state.blogs.filters;

  return blogs.filter((blog) => {
    // Status filter
    if (status && blog.status !== status) {
      return false;
    }

    // Category filter
    if (category && blog.category !== category) {
      return false;
    }

    // Search filter
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const matchesSearch =
        blog.title?.toLowerCase().includes(lower) ||
        blog.author?.toLowerCase().includes(lower) ||
        blog.slug?.toLowerCase().includes(lower);
      if (!matchesSearch) return false;
    }

    return true;
  });
};

export const selectPaginatedAdminBlogs = (state) => {
  const filtered = selectFilteredAdminBlogs(state);
  const { currentPage, itemsPerPage } = state.blogs.pagination;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return filtered.slice(startIndex, endIndex);
};

export const selectAdminBlogsTotalPages = (state) => {
  const filtered = selectFilteredAdminBlogs(state);
  const { itemsPerPage } = state.blogs.pagination;
  return Math.ceil(filtered.length / itemsPerPage);
};

export default blogSlice.reducer;

